<template>
  <div
    :class="[
      $style.root,
      size === 'sm' && $style.rootSm,
      size === 'md' && $style.rootMd,
      size === 'lg' && $style.rootLg,
    ]"
  >
    <!-- The root only carries a CSS variable; content and backdrop are both
         position:fixed, so the root has no box of its own and reads as hidden.
         Assert visibility on this instead. -->
    <div
      :class="[$style.content, 'Popup__content', classes?.content]"
      data-testid="popup-content"
    >
      <button
        type="button"
        :class="$style.close"
        :aria-label="closeAriaLabel"
        @click="emit('close', 'button')"
      />
      <div :class="[$style.scrollable, classes?.scrollable]">
        <slot />
      </div>
    </div>
    <i
      :class="[$style.backdrop, classes?.backdrop]"
      data-testid="popup-backdrop"
      @click="emit('close', 'backdrop')"
      @touchend.prevent="emit('close', 'backdrop')"
    />
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted } from 'vue';

export type UiPopupSize = 'sm' | 'md' | 'lg';
export type UiPopupCloseReason = 'button' | 'backdrop' | 'escape';

interface Props {
  size?: UiPopupSize;
  classes?: {
    content?: string;
    scrollable?: string;
    backdrop?: string;
  };
  closeAriaLabel?: string;
}

withDefaults(defineProps<Props>(), {
  size: undefined,
  classes: undefined,
  closeAriaLabel: 'Close',
});

const emit = defineEmits<{
  close: [reason: UiPopupCloseReason];
}>();

// Each open popup has its own listener. Thus, Escape closes all the open
// popups, also when one popup is on top of a different popup.
function onEscape(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    emit('close', 'escape');
  }
}

onMounted(() => document.addEventListener('keydown', onEscape));
onBeforeUnmount(() => document.removeEventListener('keydown', onEscape));
</script>

<style module>
.root {
  --ui-popup-scrollbar-width: 1rem;
}

.close {
  position: absolute;
  top: var(--kiosk-popup-close-top);
  right: var(--kiosk-popup-close-right);
  background: none;
  border: 0;
  padding: 0;
  margin: 0;
  width: var(--kiosk-popup-close-size);
  height: var(--kiosk-popup-close-size);
  cursor: pointer;
}

.close::after {
  content: '×';
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 200;
  font-family: var(--Global--font);
  font-size: var(--kiosk-popup-close-font-size);
  line-height: 1;
  color: var(--Kiosk--popup_font_color);
  transform: translateY(-5%);
}

.content {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--Kiosk--popup_background_color);
  color: var(--Kiosk--popup_font_color);
  z-index: 30;
  box-shadow: -1.11rem 1.11rem 1.11rem -0.56rem rgba(0, 0, 0, 0.2);
  border-radius: 0.89rem;
  box-sizing: border-box;
  text-shadow: none;
  padding: calc(var(--ui-popup-scrollbar-width) * 0.3) 0;
  width: var(
    --ui-popup-content-width,
    var(--kiosk-popup-content-width, var(--ui-popup-size-width, min-content))
  );
  max-width: var(
    --ui-popup-content-max-width,
    var(--kiosk-popup-content-max-width, none)
  );
}

.rootSm {
  --ui-popup-size-width: 24rem;
}

.rootMd {
  --ui-popup-size-width: 52.22rem;
}

.rootLg {
  --ui-popup-size-width: 68.89rem;
}

.scrollable {
  padding: var(--kiosk-popup-scrollable-padding);
  max-height: var(--kiosk-popup-scrollable-max-height);
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--ui-popup-scrollbar-width) transparent;
}

.scrollable::-webkit-scrollbar {
  width: var(--ui-popup-scrollbar-width);
  background: transparent;
}

.scrollable::-webkit-scrollbar-thumb {
  background-color: rgba(var(--Kiosk--tint-color-rgb), 0.3);
  border-radius: var(--ui-popup-scrollbar-width);
  border: calc(var(--ui-popup-scrollbar-width) * 0.25) solid transparent;
  background-clip: content-box;
}

.backdrop {
  position: fixed;
  inset: 0;
  background: rgba(var(--Kiosk--tint-color-rgb), 0.65);
  z-index: 20;
}
</style>
